import { useMemo } from 'react';

import useNavigation from '@/hooks/useNavigation';

import useCopyRouteToClipboardEvent from '../../events/CopyRouteToClipboard';
import useOpenLinkEvent from '../../events/OpenLink';
import { EventHandlerActionsType } from '../../types';
import {
  AnyComponentType,
  AnyStateType,
  BaseEventsTypes,
  ExecuteFunctionEventType,
  HideComponentEventType,
  PushNavigationEventType,
  RemoveUiStateEventType,
  ReplaceNavigationEventType,
  SetUiStateEventType,
  ShowComponentEventType,
} from './events';

const usePushNavigateEvent = () => {
  const { push } = useNavigation();
  return (event: PushNavigationEventType) => {
    const { params } = event;
    const { url } = params;
    push(url);
  };
};

const useReplaceNavigateEvent = () => {
  const { replace } = useNavigation();
  return (event: ReplaceNavigationEventType) => {
    const { params } = event;
    const { url } = params;
    replace(url);
  };
};

const useBaseEvents = <S extends AnyStateType, C extends AnyComponentType>(): {
  [key in BaseEventsTypes<S, C>]: (event: any, actions: EventHandlerActionsType<S, C>) => void;
} => {
  const onExecuteFunction = (event: ExecuteFunctionEventType) => {
    const { params } = event;
    const { function: func, args } = params;
    func(...args);
  };

  const onPushNavigate = usePushNavigateEvent();
  const onReplaceNavigate = useReplaceNavigateEvent();
  const onOpenLink = useOpenLinkEvent();
  const onCopyRouteToClipboard = useCopyRouteToClipboardEvent();

  return useMemo(() => {
    const onShowComponent = (
      event: ShowComponentEventType<C>,
      { componentsActions }: EventHandlerActionsType<S, C>,
    ) => {
      const { params } = event;
      const { Component, props } = params;
      componentsActions.set(Component, props);
    };

    const onHideComponent = (
      event: HideComponentEventType<C>,
      { componentsActions }: EventHandlerActionsType<S, C>,
    ) => {
      const { params } = event;
      const { Component } = params;
      componentsActions.remove(Component);
    };

    const onSetUIState = (
      event: SetUiStateEventType<S>,
      { eventHandlerUIActions }: EventHandlerActionsType<S, C>,
    ) => {
      const { params } = event;
      const { key, value } = params;
      eventHandlerUIActions.set(key, value);
    };

    const onRemoveUIState = (
      event: RemoveUiStateEventType,
      { eventHandlerUIActions }: EventHandlerActionsType<S, C>,
    ) => {
      const { params } = event;
      const { key } = params;
      eventHandlerUIActions.remove(key);
    };

    return {
      'show-component': onShowComponent,
      'hide-component': onHideComponent,
      'set-ui-state': onSetUIState,
      'remove-ui-state': onRemoveUIState,
      'execute-function': onExecuteFunction,
      'push-navigation': onPushNavigate,
      'replace-navigation': onReplaceNavigate,
      'open-link': onOpenLink,
      'copy-route-to-clipboard': onCopyRouteToClipboard,
    };
  }, [onCopyRouteToClipboard, onOpenLink, onPushNavigate, onReplaceNavigate]);
};

export default useBaseEvents;
